import React, { ReactNode } from 'react';

interface MarkdownMessageProps {
  children: string;
}

const renderInline = (text: string, keyPrefix: string): ReactNode[] => {
  const tokens = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*\n]+\*|_[^_\n]+_|\[[^\]]+\]\([^\s)]+\))/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match = tokens.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**') || token.startsWith('__')) {
      parts.push(<strong key={`${keyPrefix}-${match.index}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={`${keyPrefix}-${match.index}`}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('*') || token.startsWith('_')) {
      parts.push(<em key={`${keyPrefix}-${match.index}`}>{token.slice(1, -1)}</em>);
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^\s)]+)\)$/);
      if (link) {
        // Discussion citations are rendered by the server-derived provenance block.
        // Do not let model-authored Markdown create arbitrary navigation targets.
        parts.push(link[1]);
      } else {
        parts.push(token);
      }
    }
    lastIndex = match.index + token.length;
    match = tokens.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

const renderParagraph = (lines: string[], key: string) => (
  <p key={key}>
    {(() => {
      let offset = 0;
      return lines.map((line, lineIndex) => {
        const lineKey = `${key}-${offset}`;
        offset += line.length + 1;
        return (
          <React.Fragment key={lineKey}>
            {lineIndex > 0 && <br />}
            {renderInline(line, lineKey)}
          </React.Fragment>
        );
      });
    })()}
  </p>
);

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ children }) => {
  const lines = children.replace(/\r\n?/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push(renderParagraph(paragraph, `paragraph-${blocks.length}`));
      paragraph = [];
    }
  };

  const flushList = () => {
    if (!list) { return; }
    const List = list.ordered ? 'ol' : 'ul';
    let offset = 0;
    blocks.push(
      <List key={`list-${blocks.length}`}>
        {list.items.map(item => {
          const itemKey = `list-item-${offset}`;
          offset += item.length + 1;
          return <li key={itemKey}>{renderInline(item, itemKey)}</li>;
        })}
      </List>,
    );
    list = null;
  };

  lines.forEach((line) => {
    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    const orderedItem = line.match(/^\s*\d+\.\s+(.+)$/);
    const unorderedItem = line.match(/^\s*[-*+]\s+(.+)$/);

    if (!line.trim()) {
      flushParagraph();
      flushList();
    } else if (heading) {
      flushParagraph();
      flushList();
      const Heading = `h${heading[1].length}` as keyof JSX.IntrinsicElements;
      blocks.push(<Heading key={`heading-${blocks.length}`}>{renderInline(heading[2], `heading-${blocks.length}`)}</Heading>);
    } else if (orderedItem || unorderedItem) {
      flushParagraph();
      const ordered = Boolean(orderedItem);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push((orderedItem || unorderedItem)?.[1] || '');
    } else {
      flushList();
      paragraph.push(line);
    }
  });

  flushParagraph();
  flushList();
  return <div className="unit-condenser-plan-shell__markdown">{blocks}</div>;
};

export default MarkdownMessage;
